/**
 * Quick Add to Cart Action
 * Triggered by: arc:action:quickAddToCart
 * 
 * A simplified add to cart that relies entirely on product context
 * Useful for quick add buttons on product tiles/cards
 */

export const runQuickAddToCart = async (data = {}) => {
    try {
        // Handle both single object and array of objects
        const items = Array.isArray(data) ? data : [data];
        
        // Get shared settings from first item (for backward compatibility)
        const {
            sections = [],
            sections_url = window.location.pathname
        } = items[0] || {};

        // Process each item
        const processedItems = items.map(item => {
            const {
                quantity = 1,
                properties = {},
                variantId: providedVariantId
            } = item;

            let variantId = providedVariantId;

            // If no variantId provided, get it from product context
            if (!variantId) {
                const productContext = window.getProductContext?.();
                
                if (!productContext || !productContext.variantId) {
                    throw new Error('No product context available. Make sure you are on a product page or hovering over a product.');
                }

                variantId = productContext.variantId;
            }

            return {
                id: variantId,
                quantity,
                properties,
                originalVariantId: variantId
            };
        });

        // Log context for debugging
        console.log('Quick add to cart using context:', {
            items: processedItems,
            sections,
            sections_url
        });

        // Prepare cart data
        const cartData = {
            items: processedItems.map(({ id, quantity, properties }) => ({
                id,
                quantity,
                properties
            })),
            sections: sections,
            sections_url: sections_url
        };

        // Add to cart via Shopify Cart API
        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(cartData)
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to add to cart');
        }

        const result = await response.json();

        // Get product context for the event (use first item's context for backward compatibility)
        const productContext = window.getProductContext?.();

        // Dispatch success event with product context
        window.dispatchEvent(new CustomEvent('cart:item_added', {
            detail: {
                item: result,
                items: processedItems,
                productContext: productContext,
                // Keep backward compatibility for single item
                ...(processedItems.length === 1 ? {
                    variantId: processedItems[0].originalVariantId,
                    quantity: processedItems[0].quantity,
                    properties: processedItems[0].properties
                } : {})
            }
        }));

        return {
            success: true,
            item: result,
            items: processedItems,
            productContext: productContext,
            message: `${processedItems.length} item${processedItems.length > 1 ? 's' : ''} added to cart successfully`
        };

    } catch (error) {
        console.error('Error in quick add to cart:', error);
        
        // Get items for error handling
        const items = Array.isArray(data) ? data : [data];
        
        // Dispatch error event
        window.dispatchEvent(new CustomEvent('cart:add_error', {
            detail: {
                error: error.message,
                action: 'quickAddToCart',
                items: items,
                // Keep backward compatibility for single item
                ...(items.length === 1 ? items[0] : {})
            }
        }));

        return {
            success: false,
            error: error.message,
            items: items
        };
    }
};

export default runQuickAddToCart;
